import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid2,
  LinearProgress,
  Paper,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from 'recharts';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DATE_RANGES = {
  TODAY: 'today',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  LAST_QUARTER: 'last_quarter',
  LAST_YEAR: 'last_year',
  PREVIOUS_YEAR: 'previous_year'
};
const DEFAULT_DATE_RANGE = DATE_RANGES.LAST_MONTH;

const toDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === 'number') {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?._seconds === 'number') {
    const converted = new Date(value._seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveGameDate = (game) => {
  // Event dates are the source-of-truth for overview date windows.
  return (
    toDate(game?.eventStartDate) ||
    toDate(game?.eventEndDate) ||
    toDate(game?.updatedAt) ||
    toDate(game?.createdAt) ||
    toDate(game?.startDate) ||
    toDate(game?.endDate) ||
    toDate(game?.date) ||
    toDate(game?.gameDate) ||
    toDate(game?.scheduledAt) ||
    toDate(game?.scheduledDate) ||
    null
  );
};

const getVenueLabel = (game, venueById = {}) => {
  const venue = game?.venue;
  if (!venue) return 'Unknown Venue';

  if (typeof venue === 'string') {
    const resolvedVenue = venueById[venue];
    if (resolvedVenue) {
      return (
        resolvedVenue.name ||
        resolvedVenue.clubName ||
        resolvedVenue.title ||
        resolvedVenue.id ||
        'Unknown Venue'
      );
    }

    return venue;
  }

  if (venue.id && venueById[venue.id]) {
    const resolvedVenue = venueById[venue.id];
    return (
      resolvedVenue.name ||
      resolvedVenue.clubName ||
      resolvedVenue.title ||
      resolvedVenue.id ||
      'Unknown Venue'
    );
  }

  return venue.name || venue.clubName || venue.title || venue.id || 'Unknown Venue';
};

const getRinkLabel = (game) => {
  const green = game?.green ?? '';
  const rink = game?.rink ?? '';
  const value = `${green}${rink}`.trim();
  return value || 'Unknown Rink';
};

const getRinkDisplayLabel = (game) => {
  const green = game?.green;
  const rink = game?.rink;

  const greenLabel = green === undefined || green === null ? '' : String(green).trim();
  const rinkLabel = rink === undefined || rink === null ? '' : String(rink).trim();

  if (!greenLabel && !rinkLabel) {
    return getRinkLabel(game);
  }

  return `${greenLabel}${rinkLabel}`.trim();
};

const sumObjectValues = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return 0;
  }

  return Object.values(obj).reduce((sum, value) => {
    const parsed = Number(value || 0);
    return sum + (Number.isFinite(parsed) ? parsed : 0);
  }, 0);
};

const getGameShots = (game) => {
  const teamAId = game?.teams?.teamA?.id;
  const teamBId = game?.teams?.teamB?.id;
  const sets = Array.isArray(game?.sets) ? game.sets : [];
  let totalShots = 0;

  sets.forEach((set) => {
    if (Array.isArray(set?.scores) && set.scores.length > 0) {
      set.scores.forEach((score) => {
        totalShots += sumObjectValues(score?.teamScores);
      });
      return;
    }

    if (set?.totalSetScore) {
      const setTotalA = Number(set.totalSetScore?.[teamAId] || 0);
      const setTotalB = Number(set.totalSetScore?.[teamBId] || 0);
      totalShots +=
        (Number.isFinite(setTotalA) ? setTotalA : 0) + (Number.isFinite(setTotalB) ? setTotalB : 0);
    }
  });

  if (totalShots > 0) {
    return totalShots;
  }

  const teamTotals = game?.teamTotals || {};
  const teamATotal = Number(teamTotals?.[teamAId] || 0);
  const teamBTotal = Number(teamTotals?.[teamBId] || 0);
  const teamCombined =
    (Number.isFinite(teamATotal) ? teamATotal : 0) + (Number.isFinite(teamBTotal) ? teamBTotal : 0);

  if (teamCombined > 0) {
    return teamCombined;
  }

  return sumObjectValues(teamTotals);
};

const getStrictSetShots = (game) => {
  const sets = Array.isArray(game?.sets) ? game.sets : [];
  let totalShots = 0;

  sets.forEach((set) => {
    if (Array.isArray(set?.scores) && set.scores.length > 0) {
      set.scores.forEach((score) => {
        totalShots += sumObjectValues(score?.teamScores);
      });
      return;
    }

    if (set?.totalSetScore) {
      totalShots += sumObjectValues(set.totalSetScore);
    }
  });

  return totalShots;
};

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
};

const formatWeekLabel = (date) => `${date.getMonth() + 1}/${date.getDate()}`;
const formatMonthLabel = (date) =>
  date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
const formatHourLabel = (hour) => `${String(hour).padStart(2, '0')}:00`;

const subtractDays = (date, days) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d;
};

const getRangeStart = (now, range) => {
  const base = new Date(now);
  base.setHours(0, 0, 0, 0);

  switch (range) {
    case DATE_RANGES.TODAY:
      return base;
    case DATE_RANGES.LAST_WEEK:
      return subtractDays(base, 7);
    case DATE_RANGES.LAST_MONTH:
      return subtractDays(base, 30);
    case DATE_RANGES.LAST_QUARTER:
      return subtractDays(base, 90);
    case DATE_RANGES.LAST_YEAR:
      return subtractDays(base, 365);
    case DATE_RANGES.PREVIOUS_YEAR: {
      const yearStart = new Date(base.getFullYear() - 1, 0, 1);
      yearStart.setHours(0, 0, 0, 0);
      return yearStart;
    }
    default:
      return subtractDays(base, 30);
  }
};

const getRangeWindow = (now, range) => {
  const start = getRangeStart(now, range);

  if (range === DATE_RANGES.PREVIOUS_YEAR) {
    const endExclusive = new Date(now.getFullYear(), 0, 1);
    endExclusive.setHours(0, 0, 0, 0);
    return { start, endExclusive };
  }

  return { start, endExclusive: new Date(now.getTime() + 1) };
};

const isInDateRange = (date, now, range) => {
  if (!date) return false;
  const { start, endExclusive } = getRangeWindow(now, range);
  return date >= start && date < endExclusive;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addHours = (date, hours) => {
  const d = new Date(date);
  d.setHours(d.getHours() + hours);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const buildTrendBuckets = (now, selectedDateRange) => {
  const buckets = [];

  if (selectedDateRange === DATE_RANGES.PREVIOUS_YEAR) {
    const start = new Date(now.getFullYear() - 1, 0, 1);
    start.setHours(0, 0, 0, 0);

    for (let i = 0; i < 12; i += 1) {
      const bucketStart = addMonths(start, i);
      const bucketEnd = addMonths(bucketStart, 1);
      buckets.push({
        key: bucketStart.toISOString(),
        label: formatMonthLabel(bucketStart),
        bucketStart,
        bucketEnd,
        games: 0,
        shots: 0
      });
    }

    return buckets;
  }

  if (selectedDateRange === DATE_RANGES.TODAY) {
    const dayStart = startOfDay(now);
    for (let hour = 0; hour < 24; hour += 1) {
      const bucketStart = addHours(dayStart, hour);
      const bucketEnd = addHours(bucketStart, 1);
      buckets.push({
        key: `${bucketStart.toISOString()}-${hour}`,
        label: formatHourLabel(hour),
        bucketStart,
        bucketEnd,
        games: 0,
        shots: 0
      });
    }
    return buckets;
  }

  if (selectedDateRange === DATE_RANGES.LAST_WEEK) {
    const rangeStart = getRangeStart(now, selectedDateRange);
    const start = startOfDay(rangeStart);

    for (let i = 0; i < 8; i += 1) {
      const bucketStart = addDays(start, i);
      const bucketEnd = addDays(bucketStart, 1);
      buckets.push({
        key: bucketStart.toISOString(),
        label: bucketStart.toLocaleDateString(undefined, { weekday: 'short' }),
        bucketStart,
        bucketEnd,
        games: 0,
        shots: 0
      });
    }
    return buckets;
  }

  if (selectedDateRange === DATE_RANGES.LAST_MONTH) {
    const rangeStart = getRangeStart(now, selectedDateRange);
    const start = startOfWeek(rangeStart);

    for (let i = 0; i < 6; i += 1) {
      const bucketStart = addDays(start, i * 7);
      const bucketEnd = addDays(bucketStart, 7);
      buckets.push({
        key: bucketStart.toISOString(),
        label: formatWeekLabel(bucketStart),
        bucketStart,
        bucketEnd,
        games: 0,
        shots: 0
      });
    }
    return buckets;
  }

  if (selectedDateRange === DATE_RANGES.LAST_QUARTER) {
    const rangeStart = getRangeStart(now, selectedDateRange);
    const start = startOfWeek(rangeStart);

    for (let i = 0; i < 14; i += 1) {
      const bucketStart = addDays(start, i * 7);
      const bucketEnd = addDays(bucketStart, 7);
      buckets.push({
        key: bucketStart.toISOString(),
        label: formatWeekLabel(bucketStart),
        bucketStart,
        bucketEnd,
        games: 0,
        shots: 0
      });
    }
    return buckets;
  }

  const rangeStart = getRangeStart(now, DATE_RANGES.LAST_YEAR);
  const start = startOfMonth(rangeStart);
  for (let i = 0; i < 12; i += 1) {
    const bucketStart = addMonths(start, i);
    const bucketEnd = addMonths(bucketStart, 1);
    buckets.push({
      key: bucketStart.toISOString(),
      label: formatMonthLabel(bucketStart),
      bucketStart,
      bucketEnd,
      games: 0,
      shots: 0
    });
  }

  return buckets;
};

const ChartTooltip = ({ active, payload, label, compact = false }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={4}
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper'
      }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {payload.map((entry) => (
        <Typography
          key={entry.dataKey}
          variant={compact ? 'caption' : 'body2'}
          fontWeight={600}
          sx={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
};

const BusiestDaysChart = ({ data, colors }) => {
  const chartData = data.map((item) => ({ day: item.label, games: item.value }));

  return (
    <Box height={285}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
          <XAxis dataKey="day" tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: colors.cursor }} />
          <Bar dataKey="games" name="Games" radius={[8, 8, 4, 4]} fill={colors.primary} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const TrendLines = ({ data, subtitle, colors }) => {
  const chartData = data.map((item) => ({ period: item.label, games: item.games, shots: item.shots }));

  return (
    <Box>
      <Box mb={0.75}>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      <Box height={300}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
          <XAxis dataKey="period" tickLine={false} axisLine={false} minTickGap={14} />
          <YAxis yAxisId="games" orientation="left" allowDecimals={false} tickLine={false} axisLine={false} />
          <YAxis yAxisId="shots" orientation="right" allowDecimals={false} tickLine={false} axisLine={false} />
          <Tooltip content={<ChartTooltip />} />
          <Legend verticalAlign="top" height={28} />
          <Line
            yAxisId="games"
            type="monotone"
            dataKey="games"
            name="Games"
            stroke={colors.primary}
            strokeWidth={3}
            dot={{ r: 3.5 }}
            activeDot={{ r: 6 }}
          />
          <Line
            yAxisId="shots"
            type="monotone"
            dataKey="shots"
            name="Shots"
            stroke={colors.secondary}
            strokeWidth={3}
            dot={{ r: 3.5 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
      </Box>
    </Box>
  );
};

const RinkRankings = ({ rows, title, colors, compactEmptyMessage }) => {
  if (rows.length === 0) {
    return <Alert severity="info">No rink shot data yet for the selected filters.</Alert>;
  }

  if (rows.length === 1 && compactEmptyMessage) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {compactEmptyMessage}
        </Typography>
      </Paper>
    );
  }

  const chartData = rows.map((row) => ({ rink: row.rinkLabel, shots: row.shots }));
  const truncateLabel = (value) => {
    if (!value) return '';
    return value.length > 26 ? `${value.slice(0, 26)}...` : value;
  };
  const maxRinkLabelLength = chartData.reduce((max, row) => Math.max(max, row.rink.length), 2);
  const yAxisWidth = Math.min(78, Math.max(40, maxRinkLabelLength * 8 + 8));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" mb={1.5}>
        {title}
      </Typography>
      <Box height={290}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={colors.grid} />
            <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
            <YAxis
              dataKey="rink"
              type="category"
              width={yAxisWidth}
              tickLine={false}
              axisLine={false}
              tickFormatter={truncateLabel}
              tick={{ fontSize: 11 }}
            />
            <Tooltip content={<ChartTooltip compact={true} />} />
            <Bar dataKey="shots" name="Shots" radius={[0, 8, 8, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`rink-cell-${entry.rink}`}
                  fill={index % 2 === 0 ? colors.primary : colors.muted}
                />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const OverviewActivityInsights = ({ games, venues = [], loading }) => {
  const theme = useTheme();
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(DEFAULT_DATE_RANGE);

  const chartColors = useMemo(
    () => ({
      // Shadcn-like restrained solids (no gradients)
      primary: '#334155',
      secondary: '#0f766e',
      muted: '#64748b',
      grid: theme.palette.divider,
      cursor: theme.palette.action.hover
    }),
    [theme]
  );

  const venueById = useMemo(() => {
    return venues.reduce((acc, venue) => {
      if (venue?.id) {
        acc[venue.id] = venue;
      }
      return acc;
    }, {});
  }, [venues]);

  const venueOptions = useMemo(() => {
    return [...new Set(games.map((game) => getVenueLabel(game, venueById)))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [games, venueById]);

  useEffect(() => {
    setSelectedVenues((current) => {
      if (venueOptions.length === 0) {
        return [];
      }

      if (current.length === 0) {
        return venueOptions;
      }

      const stillValid = current.filter((venue) => venueOptions.includes(venue));
      return stillValid.length > 0 ? stillValid : venueOptions;
    });
  }, [venueOptions]);

  const filteredByVenueGames = useMemo(() => {
    if (selectedVenues.length === 0) {
      return games;
    }

    return games.filter((game) => selectedVenues.includes(getVenueLabel(game, venueById)));
  }, [games, selectedVenues, venueById]);

  const now = useMemo(() => new Date(), []);

  const rangeGames = useMemo(() => {
    return filteredByVenueGames.filter((game) => {
      const date = resolveGameDate(game);
      return isInDateRange(date, now, selectedDateRange);
    });
  }, [filteredByVenueGames, now, selectedDateRange]);

  const selectedDateRangeLabel = useMemo(() => {
    switch (selectedDateRange) {
      case DATE_RANGES.TODAY:
        return 'Today';
      case DATE_RANGES.LAST_WEEK:
        return 'Last week';
      case DATE_RANGES.LAST_MONTH:
        return 'Last month';
      case DATE_RANGES.LAST_QUARTER:
        return 'Last quarter';
      case DATE_RANGES.LAST_YEAR:
        return 'Last year';
      case DATE_RANGES.PREVIOUS_YEAR:
        return 'Previous year';
      default:
        return 'Last month';
    }
  }, [selectedDateRange]);

  const summary = useMemo(() => {
    const totalGames = rangeGames.length;
    const totalShots = rangeGames.reduce((sum, game) => sum + getGameShots(game), 0);

    const uniqueRinks = new Set(
      rangeGames.map((game) => `${getVenueLabel(game, venueById)} | ${getRinkLabel(game)}`)
    ).size;

    return {
      totalGames,
      totalShots,
      avgShots: totalGames > 0 ? (totalShots / totalGames).toFixed(1) : '0.0',
      uniqueRinks
    };
  }, [rangeGames, venueById]);

  const busiestDays = useMemo(() => {
    const dayCounts = Array(7).fill(0);

    rangeGames.forEach((game) => {
      const date = resolveGameDate(game);
      if (!date) return;
      dayCounts[date.getDay()] += 1;
    });

    return DAYS.map((day, index) => ({ label: day, value: dayCounts[index] }));
  }, [rangeGames]);

  const trendData = useMemo(() => {
    const rows = buildTrendBuckets(now, selectedDateRange);

    rangeGames.forEach((game) => {
      const date = resolveGameDate(game);
      if (!date) return;

      const index = rows.findIndex((row) => date >= row.bucketStart && date < row.bucketEnd);
      if (index === -1) return;

      rows[index].games += 1;
      rows[index].shots += getGameShots(game);
    });

    return rows;
  }, [rangeGames, now, selectedDateRange]);

  const trendSubtitle = useMemo(() => {
    switch (selectedDateRange) {
      case DATE_RANGES.TODAY:
        return 'Hourly trend for today';
      case DATE_RANGES.LAST_WEEK:
        return 'Daily trend for the last week';
      case DATE_RANGES.LAST_MONTH:
        return 'Weekly trend for the last month';
      case DATE_RANGES.LAST_QUARTER:
        return 'Weekly trend for the last quarter';
      case DATE_RANGES.LAST_YEAR:
        return 'Monthly trend for the last year';
      case DATE_RANGES.PREVIOUS_YEAR:
        return 'Monthly trend for the previous calendar year';
      default:
        return 'Trend over time';
    }
  }, [selectedDateRange]);

  const rinkShotRows = useMemo(() => {
    const map = new Map();

    rangeGames.forEach((game) => {
      const strictShots = getStrictSetShots(game);
      const gameShots = strictShots > 0 ? strictShots : getGameShots(game);
      if (gameShots <= 0) {
        return;
      }

      const venueLabel = getVenueLabel(game, venueById);
      const rinkLabel = getRinkDisplayLabel(game);
      const key = `${venueLabel}::${rinkLabel}`;
      const current = map.get(key) || { venue: venueLabel, rinkLabel, shots: 0 };
      map.set(key, { ...current, shots: current.shots + gameShots });
    });

    return Array.from(map.values());
  }, [rangeGames, venueById]);

  const topRinksByVenue = useMemo(() => {
    const byVenue = new Map();

    rinkShotRows.forEach((row) => {
      const rows = byVenue.get(row.venue) || [];
      rows.push(row);
      byVenue.set(row.venue, rows);
    });

    return Array.from(byVenue.entries())
      .map(([venue, rows]) => ({
        venue,
        rows: [...rows].sort((a, b) => b.shots - a.shots).slice(0, 10)
      }))
      .sort((a, b) => a.venue.localeCompare(b.venue));
  }, [rinkShotRows]);

  const lowestRinksByVenue = useMemo(() => {
    return topRinksByVenue.map((venueGroup) => ({
      venue: venueGroup.venue,
      rows: [...venueGroup.rows]
        .filter((row) => row.shots > 0)
        .sort((a, b) => a.shots - b.shots)
        .slice(0, 10)
    }));
  }, [topRinksByVenue]);

  const toggleVenue = (venueName) => {
    setSelectedVenues((current) => {
      if (current.includes(venueName)) {
        const next = current.filter((venue) => venue !== venueName);
        return next.length === 0 ? current : next;
      }

      return [...current, venueName];
    });
  };

  const selectAllVenues = () => {
    setSelectedVenues(venueOptions);
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6">Workspace Activity</Typography>
        <Typography variant="body2" color="text.secondary">
          Loading workspace analytics...
        </Typography>
      </Paper>
    );
  }

  if (games.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6">Workspace Activity</Typography>
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No games found yet. Start scoring to unlock activity stats and trends.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Workspace Activity
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Analytics based on your selected venues/clubs and selected date range.
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" mb={1}>
            Date Range
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label="Today"
              size="small"
              color={selectedDateRange === DATE_RANGES.TODAY ? 'primary' : 'default'}
              variant={selectedDateRange === DATE_RANGES.TODAY ? 'filled' : 'outlined'}
              onClick={() => setSelectedDateRange(DATE_RANGES.TODAY)}
            />
            <Chip
              label="Last week"
              size="small"
              color={selectedDateRange === DATE_RANGES.LAST_WEEK ? 'primary' : 'default'}
              variant={selectedDateRange === DATE_RANGES.LAST_WEEK ? 'filled' : 'outlined'}
              onClick={() => setSelectedDateRange(DATE_RANGES.LAST_WEEK)}
            />
            <Chip
              label="Last month"
              size="small"
              color={selectedDateRange === DATE_RANGES.LAST_MONTH ? 'primary' : 'default'}
              variant={selectedDateRange === DATE_RANGES.LAST_MONTH ? 'filled' : 'outlined'}
              onClick={() => setSelectedDateRange(DATE_RANGES.LAST_MONTH)}
            />
            <Chip
              label="Last quarter"
              size="small"
              color={selectedDateRange === DATE_RANGES.LAST_QUARTER ? 'primary' : 'default'}
              variant={selectedDateRange === DATE_RANGES.LAST_QUARTER ? 'filled' : 'outlined'}
              onClick={() => setSelectedDateRange(DATE_RANGES.LAST_QUARTER)}
            />
            <Chip
              label="Last year"
              size="small"
              color={selectedDateRange === DATE_RANGES.LAST_YEAR ? 'primary' : 'default'}
              variant={selectedDateRange === DATE_RANGES.LAST_YEAR ? 'filled' : 'outlined'}
              onClick={() => setSelectedDateRange(DATE_RANGES.LAST_YEAR)}
            />
            <Chip
              label="Previous year"
              size="small"
              color={selectedDateRange === DATE_RANGES.PREVIOUS_YEAR ? 'primary' : 'default'}
              variant={selectedDateRange === DATE_RANGES.PREVIOUS_YEAR ? 'filled' : 'outlined'}
              onClick={() => setSelectedDateRange(DATE_RANGES.PREVIOUS_YEAR)}
            />
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2" mb={1}>
            Venue/Club Filter (Chart 1 supports multi-select)
          </Typography>
          <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
            <Chip
              label="Select All"
              color="primary"
              variant="outlined"
              onClick={selectAllVenues}
              size="small"
            />
            <Chip
              label={`${selectedVenues.length} selected`}
              color="default"
              variant="outlined"
              size="small"
            />
          </Stack>

          <FormGroup row>
            {venueOptions.map((venue) => (
              <FormControlLabel
                key={venue}
                control={
                  <Checkbox
                    checked={selectedVenues.includes(venue)}
                    onChange={() => toggleVenue(venue)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{venue}</Typography>}
              />
            ))}
          </FormGroup>
        </Box>

        <Grid2 container spacing={1.5}>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total Games ({selectedDateRangeLabel})
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {summary.totalGames}
              </Typography>
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Total Shots ({selectedDateRangeLabel})
                </Typography>
                <MuiTooltip
                  arrow
                  title="Total shots = Team A shots + Team B shots for each game, then summed across the selected date range and venue filters.">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </MuiTooltip>
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {summary.totalShots}
              </Typography>
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Avg Shots Per Game ({selectedDateRangeLabel})
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {summary.avgShots}
              </Typography>
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Unique Rinks ({selectedDateRangeLabel})
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {summary.uniqueRinks}
              </Typography>
            </Paper>
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1.25}>
                Chart 1: Busiest Days ({selectedDateRangeLabel})
              </Typography>
              <BusiestDaysChart data={busiestDays} colors={chartColors} />
            </Paper>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1.25}>
                Chart 2: Trend of Play + Total Shots
              </Typography>
              <TrendLines data={trendData} subtitle={trendSubtitle} colors={chartColors} />
            </Paper>
          </Grid2>
        </Grid2>

        {rinkShotRows.length === 0 && (
          <Alert severity="info">
            No rink shot data was found for the current date range and venue filters.
          </Alert>
        )}

        <Typography variant="subtitle1" fontWeight={700}>
          Top 10 Rinks by Venue ({selectedDateRangeLabel})
        </Typography>
        <Grid2 container spacing={2}>
          {topRinksByVenue.map((venueGroup) => (
            <Grid2 key={`top-${venueGroup.venue}`} size={{ xs: 12, md: 6 }}>
              <RinkRankings rows={venueGroup.rows} title={venueGroup.venue} colors={chartColors} />
            </Grid2>
          ))}
        </Grid2>

        <Typography variant="subtitle1" fontWeight={700}>
          Lowest 10 Rinks by Venue ({selectedDateRangeLabel})
        </Typography>
        <Grid2 container spacing={2}>
          {lowestRinksByVenue.map((venueGroup) => (
            <Grid2 key={`low-${venueGroup.venue}`} size={{ xs: 12, md: 6 }}>
              <RinkRankings
                rows={venueGroup.rows}
                title={venueGroup.venue}
                colors={chartColors}
                compactEmptyMessage="Only one rink has shot data for this venue in the selected range."
              />
            </Grid2>
          ))}
        </Grid2>
      </Stack>
    </Paper>
  );
};

export default OverviewActivityInsights;
